import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useModals } from "@/context/ModalContext";
import { api } from "@/lib/api";
import { PHONE, PHONE_HREF, EMAIL } from "@/lib/config";
import { Check, Phone, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";

const field = "w-full bg-charcoal border border-white/15 px-3 py-2.5 text-sm text-cream outline-none focus:border-gold placeholder:text-cream/40 rounded-md";

export const QuoteModal = () => {
  const { quote, closeQuote } = useModals();
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", purpose: "", message: "" });
  const open = !!quote;
  const purpose = form.purpose || quote?.purpose || "";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.postQuote({ ...form, purpose });
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  const close = () => {
    closeQuote();
    setTimeout(() => { setDone(false); setForm({ name: "", email: "", phone: "", purpose: "", message: "" }); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="quote-modal" aria-describedby={undefined}
        className="bg-espresso border-white/10 text-cream sm:max-w-md">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-cream flex items-center gap-2">
                <Sparkles size={18} className="text-gold" /> Request a Quote
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-cream/60 text-sm -mt-1">
              Tell us what you're after and we'll send tailored pricing — no obligation.
            </DialogDescription>
            <form onSubmit={submit} className="space-y-3 mt-2">
              <input data-testid="quote-name" required placeholder="Full name" className={field}
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input data-testid="quote-email" required type="email" placeholder="Email" className={field}
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input data-testid="quote-phone" required placeholder="Phone number" className={field}
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <div>
                <label className="text-cream/50 text-xs mb-1 block">What it's for</label>
                <input data-testid="quote-purpose" required placeholder="e.g. Barrel sauna for backyard"
                  className={field} value={purpose}
                  onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
              </div>
              <textarea data-testid="quote-message" rows={3} placeholder="Anything else we should know? (optional)"
                className={field} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <button data-testid="quote-submit" type="submit" disabled={submitting}
                className="w-full bg-gold text-espresso py-3 font-medium rounded-md hover:bg-amber transition-colors disabled:opacity-50">
                {submitting ? "Sending…" : "Request My Quote"}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="quote-success">
            <div className="w-14 h-14 rounded-full bg-forest/20 flex items-center justify-center mx-auto mb-4">
              <Check className="text-forest" size={28} />
            </div>
            <h3 className="font-serif text-2xl">Thank you, {form.name.split(" ")[0]}.</h3>
            <p className="text-cream/65 text-sm mt-2">Your quote request is in. Our team will reach out with tailored pricing within one business day.</p>
            <a href={PHONE_HREF} className="mt-5 flex items-center justify-center gap-2 text-cream/70 hover:text-gold text-sm">
              <Phone size={14} /> Prefer to talk? Call {PHONE}
            </a>
            <a href={`mailto:${EMAIL}`} className="mt-2 flex items-center justify-center gap-2 text-cream/70 hover:text-gold text-sm">
              <Mail size={14} /> {EMAIL}
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
